import { type HTMLAttributes, type PropsWithChildren } from "react";
import Layout from "@/components/common/Layout";
import HorizontalHeader from "@/components/site-builder/BaseModule/HorizontalHeader";
import {
  SiteBuilderModuleHeaderPosition,
  SiteBuilderModuleHeaderVariant,
  SiteBuilderModuleLayoutSize,
} from "@/constants/site-builder";
import type { BaseSiteBuilderModule } from "@/types/site-builder";
import CallToActionGroup from "../CallToActionGroup";
import TextField from "../TextField";
import HeaderVariant1 from "./HeaderVariant1";
import HeaderVariant2 from "./HeaderVariant2";
import Wrapper from "./Wrapper";

export interface BaseModuleProps
  extends Pick<HTMLAttributes<HTMLElement>, "className"> {
  module: BaseSiteBuilderModule;
}

export default function BaseModule(props: PropsWithChildren<BaseModuleProps>) {
  const { module, className, children } = props;
  const {
    baseContent: {
      header: { enabled: headerEnabled },
      footer: { enabled: footerEnabled, outro },
      buttons: {
        enabled: footerButtonsEnabled,
        alignment: footerButtonsAlignment,
        buttons: footerButtons,
      },
    },
    style: {
      display: { headerPosition, headerVariant, layoutSize },
    },
  } = module;

  const headerContent = headerEnabled && (
    <>
      {headerVariant === SiteBuilderModuleHeaderVariant.Variant1 && (
        <HeaderVariant1 module={module} />
      )}
      {headerVariant === SiteBuilderModuleHeaderVariant.Variant2 && (
        <HeaderVariant2 module={module} />
      )}
    </>
  );
  const fluidLayout = layoutSize === SiteBuilderModuleLayoutSize.FullScreen;

  return (
    <Wrapper module={module} className={className}>
      {headerPosition === SiteBuilderModuleHeaderPosition.Top ? (
        <>
          {headerContent && <Layout containerOnly>{headerContent}</Layout>}
          <Layout fluid={fluidLayout} containerOnly>
            {children}
          </Layout>
        </>
      ) : (
        <HorizontalHeader
          module={module}
          headerContent={headerContent}
          fluid={fluidLayout}
        >
          {children}
        </HorizontalHeader>
      )}
      <Layout containerOnly>
        {footerEnabled && outro.text && (
          <TextField field={outro} type="outro" />
        )}
        {footerButtonsEnabled && footerButtons.length > 0 && (
          <CallToActionGroup
            buttons={footerButtons}
            position={footerButtonsAlignment}
          />
        )}
      </Layout>
    </Wrapper>
  );
}
